import { CartItem } from "@sections/cart";
import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Fragment } from "preact";
import MiniCartPrice from "./MiniCartPrice";
import { formatMoney } from "@helpers/currency";

// [TODO] This needs a heavy refactor - DAN

function getVariantMetafieldData(variantId: string) {
  const url = `https://${window.themeVars.store.permanent_domain}/api/${window.themeVars.config.storefrontApiVersion}/graphql`;
  const query = `
  query {
    node(id: "gid://shopify/ProductVariant/${variantId}") {
      ... on ProductVariant {
        id
        title
        metafields(identifiers: [
          {namespace: "brewdog", key: "max_quantity"},
          {namespace: "brewdog", key: "minimum_quantity"},
          {namespace: "brewdog", key: "parent_product_handle"}
        ]) {
          key
          value
        }
      }
    }
  }
`;

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Shopify-Storefront-Access-Token":
        window.themeVars.config.storefrontAccessToken,
    },
    body: JSON.stringify({ query }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      if (data.errors) {
        console.error("GraphQL Errors:", data.errors);
        throw new Error("GraphQL query failed");
      }
      return data?.data?.node;
    })
    .catch((error) => {
      console.error("Error fetching variant data:", error);
      return null;
    });
}

interface Product {
  title: string;
  handle: string;
  tags: string[];
  variants: {
    id: string;
    available: boolean;
    compare_at_price: string;
  }[];
}

interface Variant {
  id: string;
  available: boolean;
  compare_at_price: string;
}

function formatDispatchDate(isoDate: string): string {
  if (!isoDate) return "";
  const parts = isoDate.split("-").map((p) => parseInt(p, 10));
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  if (isNaN(year) || isNaN(month) || isNaN(day)) return isoDate;

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const getOrdinal = (d: number): string => {
    const v = d % 100;
    if (v >= 11 && v <= 13) return "th";
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const monthName = monthNames[Math.max(0, Math.min(11, month - 1))];
  return `${day}${getOrdinal(day)} ${monthName} ${year}`;
}

interface MiniCartItemProps {
  item: CartItem;
  methods?: {
    increaseQuantity: (item: string, quantity: number, max: number) => void;
    decreaseQuantity: (item: string, quantity: number, min: number) => void;
    removeItem: (item: string) => void;
  };
  metafields?: any[];
}

function MiniCartItem({
  item,
  methods = {
    increaseQuantity: () => {},
    decreaseQuantity: () => {},
    removeItem: () => {},
  },
  metafields = [],
}: MiniCartItemProps) {
  const { increaseQuantity, decreaseQuantity, removeItem } = methods;
  const [product, setProduct] = useState<Product | null>(null);
  const [variant, setVariant] = useState<Variant | null>(null);
  const [maxQuantity, setMaxQuantity] = useState<number>(1);
  const [minQuantity, setMinQuantity] = useState<number>(1);
  const [parentProductHandle, setParentProductHandle] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProductData() {
      const response = await fetch(`/products/${item.handle}.js`);
      const data = await response.json();
      setProduct(data);
    }

    fetchProductData();
  }, [item.handle]);

  const maxPreOrderQuantity = metafields.find(
    (field: any) => field.key === "maximum_quantity",
  );

  const minPreOrderQuantity = metafields.find(
    (field: any) => field.key === "minimum_quantity",
  );

  useEffect(() => {
    async function fetchVariantData() {
      // For bundle items, we need to get the variant ID from the properties
      let variantId = item.variant_id;

      // Check if this is a bundle item
      if (
        item.properties &&
        typeof item.properties["_variant_id"] === "string"
      ) {
        variantId = item.properties["_variant_id"];
      }

      const variantData = await getVariantMetafieldData(variantId);

      if (!variantData) {
        console.error("No variant data received for ID:", variantId);
        return;
      }

      // First check for max_quantity metafield
      const maxQuantityMetafield = variantData?.metafields?.find(
        (metafield: { key: string; value: string } | null) =>
          metafield && metafield.key === "max_quantity",
      );

      // If max_quantity metafield exists, use its value, otherwise default to 100
      const maxQty = maxPreOrderQuantity?.value
        ? parseInt(maxPreOrderQuantity.value)
        : maxQuantityMetafield?.value
          ? parseInt(maxQuantityMetafield.value)
          : 100;

      // Check for minimum_quantity metafield
      const minQuantityMetafield = variantData?.metafields?.find(
        (metafield: { key: string; value: string } | null) =>
          metafield && metafield.key === "minimum_quantity",
      );

      // If minimum_quantity metafield exists, use its value, otherwise default to 1
      const minQty = minPreOrderQuantity?.value
        ? parseInt(minPreOrderQuantity.value)
        : minQuantityMetafield?.value
          ? parseInt(minQuantityMetafield.value)
          : 1;

      setMaxQuantity(maxQty);
      setMinQuantity(minQty);

      // check to see if the product has a parent product handle, i.e is an inner bundle item
      const parentHandleMetafield = variantData?.metafields?.find(
        (metafield: { key: string; value: string } | null) =>
          metafield && metafield.key === "parent_product_handle",
      );

      if (parentHandleMetafield?.value) {
        //setParentProductHandle("/products/" + parentHandleMetafield.value);
        setParentProductHandle(("/products/" + parentHandleMetafield.value).replace(/\/\//g, '/'));
      
      }
    }

    fetchVariantData();
  }, [item.variant_id, item.properties]);

  const findPreOrderItem = (array: string[]) => {
    const preOrderRegex = /pre-order/i;
    return array.find((item) => preOrderRegex.test(item));
  };

  const handleQuantityIncrease = (item: CartItem) => {
    if (item.quantity >= maxQuantity) {
      if (!errorMessage) {
        setErrorMessage(
          `${window.themeVars.strings.product.maxQuantityReached.replace("&#39;", "'")} ${item.title}`,
        );
        setTimeout(() => setErrorMessage(null), 3000);
      }
      return;
    }
    increaseQuantity(item.id, item.quantity, maxQuantity);

    const swatches = document.getElementById("swatches");
    if (swatches) {
      const variantQuantityInput = swatches.querySelector(
        `[name='quantity'][data-id='${item.variant_id}']`,
      ) as HTMLInputElement;
      if (variantQuantityInput) {
        variantQuantityInput.max = (
          parseInt(variantQuantityInput.max) - 1
        ).toString();
      }
    }
  };

  const handleQuantityDecrease = (item: CartItem) => {
    if (item.quantity <= minQuantity) {
      return;
    }
    decreaseQuantity(item.id, item.quantity, minQuantity);

    const swatches = document.getElementById("swatches");
    if (swatches) {
      const variantQuantityInput = swatches.querySelector(
        `[name='quantity'][data-id='${item.variant_id}']`,
      ) as HTMLInputElement;
      if (variantQuantityInput) {
        variantQuantityInput.max = (
          parseInt(variantQuantityInput.max) + 1
        ).toString();
      }
    }
  };

  let preOrderTag;
  let preOrderDate;

  let showPreOrderMessage = false;

  if (product) {
    preOrderTag = findPreOrderItem(product.tags);

    const currentVariant = product.variants.find(
      (variant: { id: string }) => variant.id === item.variant_id,
    );
    if (currentVariant) {
      setVariant(currentVariant);
    }
  }

  if (preOrderTag) {
    preOrderDate = preOrderTag.split(":")[1];
  }

  if (preOrderTag && variant?.available) {
    showPreOrderMessage = true;
  }

  const isStaff = window.themeVars.customer.staff;
  const staffDiscount = window.themeVars.customer.staffDiscount;

  let showStaffMessaging = false;

  const isEfpMember = window.themeVars.customer.efpMember;
  const efpDiscount = window.themeVars.customer.efpDiscount;

  let comparePrice: number | null = null;
  const price = item.line_price as string | number;

  let showEfpMessaging = false;

  if (variant) {
    comparePrice = Number(variant.compare_at_price) * item.quantity;
  }

  if (isStaff && staffDiscount && !comparePrice) {
    showStaffMessaging = true;
  } else if (isEfpMember && efpDiscount && !comparePrice) {
    showEfpMessaging = true;
  }

  typeof item.properties._proof === "string" &&
    (item.image = item.properties._proof);

  const isPersonalisedCan = typeof item.properties._proof === "string";

  const imageClass = isPersonalisedCan
    ? "minicart__item-image minicart__item-image--proof"
    : "minicart__item-image";

  let isPfandCharge = false;
  const lowercaseProductTitle = item.title.toLowerCase();

  if (lowercaseProductTitle.includes("pfand")) {
    isPfandCharge = true;
  }

  const LinkTag = isPfandCharge ? "div" : "a";

  return (
    <div className="minicart__item">
      <LinkTag
        href={
          isPfandCharge
            ? undefined
            : parentProductHandle
              ? parentProductHandle
              : item.url
        }
        className="minicart__item-image-column"
      >
        <img src={item.image} alt={item.title} className={imageClass} />
      </LinkTag>

      <div className="minicart__item-info">
        <div className="minicart__item-info-top">
          <div className="minicart__item-info-top-left">
            <LinkTag
              href={
                isPfandCharge
                  ? undefined
                  : parentProductHandle
                    ? parentProductHandle
                    : item.url
              }
              className="minicart__item-title heading-4"
            >
              {item.product_title}
            </LinkTag>

            <div className="minicart__item-options">
              {!item.product_has_only_default_variant &&
                item.options_with_values.map(
                  (option: { [key: string]: string }) => (
                    <p
                      key={option.name}
                      className="minicart__item-option text-sm-body"
                    >
                      {option.value}
                    </p>
                  ),
                )}
              {!isPersonalisedCan && (
                <span>
                  {Object.entries(item.properties).map(([key, value]) => {
                    if (key.startsWith("_")) {
                      return null;
                    }
                    const numericValue =
                      typeof value === "string"
                        ? parseFloat(value.replace(",", "."))
                        : 0;

                    const formattedValue =
                      key === "Pfand" ? formatMoney(numericValue * 100) : value;

                    const formattedKey = document.createTextNode(key);

                    return (
                      <p className={`cart-item__option text-sm-body cart-item__option-key--${key.replace(/\s+/g, '-').toLowerCase()}`} key={key}>
                        <div className="minicart__item-option-key">zzgl. {key}:</div> <div className="minicart__item-option-value">{formattedValue}</div>
                      </p>
                    );
                  })}
                </span>
              )}
            </div>
          </div>

          <div className="minicart__item-line" data-no-stock-message="">
            <MiniCartPrice
              price={price.toString()}
              compareAtPrice={comparePrice || ""}
            />
          </div>
        </div>

        {!isPfandCharge && (
          <Fragment>
            {showStaffMessaging ? (
              <div className="minicart__staff-discount">
                <span
                  dangerouslySetInnerHTML={{
                    __html: window.themeVars.icons.checkmark,
                  }}
                ></span>
                <p className="text-xs-body--semi-bold">
                  {window.themeVars.strings.cart.staffDiscountApplied}
                </p>
              </div>
            ) : showEfpMessaging ? (
              <div className="minicart__equity-discount">
                <span
                  dangerouslySetInnerHTML={{
                    __html: window.themeVars.icons.checkmark,
                  }}
                ></span>
                <p className="text-xs-body--semi-bold">
                  {window.themeVars.strings.cart.equityDiscountApplied}
                </p>
              </div>
            ) : null}

            {showPreOrderMessage && (
              <div className="minicart__pre-order">
                <div className="minicart__pre-order-badge">
                  <p className="text-xs-body--semi-bold">
                    {window.themeVars.strings.cart.preOrder}
                  </p>
                </div>
                {preOrderDate && (
                  <p className="minicart__pre-order-message text-sm-body--semi-bold">
                    {window.themeVars.strings.cart.preOrderShipDate.replace(
                      "{{ date }}",
                      preOrderDate,
                    )}
                  </p>
                )}
              </div>
            )}
          </Fragment>
        )}

        {isPersonalisedCan && (
          <div style={{ width: "100%", marginBottom: "8px", paddingLeft: "0", clear: "both" }}>
            <span className="text-s-body cart-item__personalisation-options">
              {item.properties.name}, {item.properties.message}
            </span>
          </div>
        )}

        {!isPfandCharge && (
          <div className="minicart__item-info-bottom">
            {!item.properties.free_gift && (
              <div className="minicart__actions">
                <div className="minicart__quantity">
                  <button
                    onClick={() => handleQuantityDecrease(item)}
                    type="button"
                    className="minus"
                    style={{
                      borderRadius: "50%",
                      width: "30px",
                      height: "30px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: window.themeVars.icons.minus,
                    }}
                  ></button>

                  <input
                    type="number"
                    step="1"
                    min={minQuantity}
                    max={maxQuantity}
                    readOnly
                    name="quantity"
                    value={item.quantity}
                    title="Qty"
                    className="quantity text-lg-body--semi-bold"
                  />

                  <button
                    onClick={() => handleQuantityIncrease(item)}
                    type="button"
                    className="plus"
                    style={{
                      borderRadius: "50%",
                      width: "30px",
                      height: "30px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: window.themeVars.icons.plus,
                    }}
                  ></button>
                </div>
              </div>
            )}

            <button
              onClick={() => removeItem(item.id)}
              className="minicart__delete"
            >
              <span
                className="minicart__delete-icon"
                dangerouslySetInnerHTML={{
                  __html: window.themeVars.icons.delete,
                }}
              ></span>
              <span className="visually-hidden">
                {window.themeVars.strings.cart.remove}
              </span>
            </button>
          </div>
        )}

        {errorMessage && (
          <div className="minicart__item-error-message">
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="minicart__item-info-right">
          {isPersonalisedCan && (
            <span className="text-sm-body--semi-bold">
              {
                window.themeVars.personalised_cans
                  .personalised_product_disclaimer
              }
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default MiniCartItem;
